import React, { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { Close } from '@mui/icons-material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { Box, Button, Dialog, IconButton, Typography } from '@mui/material';

import {
  gray40,
  gray80,
  orange50,
  primaryButtonBackgroundColor,
  red50,
  secondaryButtonBackgroundColor,
  tertiaryButtonBackgroundColor,
  white,
} from '../../../constants/colors';
import {
  boldMd,
  dialogTitle as dialogTitleStyle,
  regularLg,
  regularSm,
} from '../../../constants/location';
import { DictionaryColumnMapping, DictionaryMappingType } from '../../../types/dictionary';
import { PatternType, ItemListType } from '../../../types/pattern';
import { getMastersByPatternId } from '../../../api/firebase/firestore/master/readByPattern';
import { getDictionariesByPatternId } from '../../../api/firebase/firestore/dictionary/readByPattern';
import CustomLoading from '../form/CustomLoading';
import CustomSelectField from '../form/CustomSelectField';

const styles = {
  dialog: {
    '& .MuiPaper-root': {
      border: '1px solid' + gray40,
      boxSizing: 'border-box',
      margin: '16px',
      padding: '40px',
      width: '560px',
      '@media screen and (max-width:960px)': {
        padding: '32px 16px',
      },
    },
  },
  dialogHeader: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '16px',
  },
  dialogTitle: {
    ...dialogTitleStyle,
    '@media screen and (max-width:960px)': {
      ...regularLg,
    },
  },
  closeIcon: {
    height: '32px',
    width: '32px',
  },
  iconButton: {
    padding: 0,
  },
  fileSummary: {
    background: gray40,
    borderRadius: '4px',
    padding: '0 12px',
    height: '40px',
    display: 'flex',
    alignItems: 'center',
    marginBottom: '16px',
  },
  buttonWrapper: {
    display: 'flex',
    gap: '16px',
    marginTop: '24px',
  },
  cancelButton: {
    ...tertiaryButtonBackgroundColor,
    height: '44px',
    width: 'calc(50% - 8px)',
  },
  cancelButtonText: {
    ...boldMd,
    color: gray80,
  },
  submitButton: {
    height: '44px',
    width: 'calc(50% - 8px)',
    ...primaryButtonBackgroundColor,
  },
  submitButtonText: {
    ...boldMd,
    color: white,
  },
};

interface PatternItem {
  id: string;
  label: string;
}

interface Props {
  open: boolean;
  /** マスタ照合の使用済み項目を取得するための会社ID（排他制御用） */
  companyId?: string | null;
  fileName: string;
  csvColumns: string[];
  patterns: PatternType[];
  initial?: DictionaryMappingType | null;
  onClose: () => void;
  onSubmit: (data: {
    patternId: string;
    patternName: string;
    searchItemId: string;
    searchItemLabel: string;
    searchCsvColumn: string;
    columnMappings: DictionaryColumnMapping[];
  }) => Promise<void> | void;
}

const DictionaryMappingFormModal = ({
  open,
  companyId,
  fileName,
  csvColumns,
  patterns,
  initial,
  onClose,
  onSubmit,
}: Props): ReactElement => {
  const [selectedPatternId, setSelectedPatternId] = useState<string>('');
  const [searchItemId, setSearchItemId] = useState<string>('');
  const [searchCsvColumn, setSearchCsvColumn] = useState<string>('');
  const [columnMappings, setColumnMappings] = useState<DictionaryColumnMapping[]>([]);
  const [submitting, setSubmitting] = useState<boolean>(false);
  // 排他制御用: 選択中パターンでマスタ照合に使われている search_item_id の集合
  const [masterUsedItemIds, setMasterUsedItemIds] = useState<Set<string>>(new Set());
  const [dictionaryUsedItemIds, setDictionaryUsedItemIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!open) return;
    setSelectedPatternId(initial?.pattern_id ?? '');
    setSearchItemId(initial?.search_item_id ?? '');
    setSearchCsvColumn(initial?.search_csv_column ?? '');
    setColumnMappings(initial?.column_mappings ?? []);
  }, [open, initial]);

  // 指定パターンでマスタ照合に使用済みの search_item_id 集合を取得する（失敗時は空Setにフォールバック）
  const fetchMasterUsedItemIds = useCallback(
    async (patternId: string): Promise<Set<string>> => {
      if (!companyId || !patternId) return new Set();
      try {
        const masters = await getMastersByPatternId(companyId, patternId);
        return new Set(masters.map(m => m.search_item_id).filter((id): id is string => !!id));
      } catch {
        return new Set();
      }
    },
    [companyId],
  );

  // 指定パターンで「他の辞書」が使用済みの search_item_id 集合を取得する（自分自身は除外）。
  // 同じ項目に複数の辞書を割り当てると変換が競合するため、選択肢から除外する。
  const fetchDictionaryUsedItemIds = useCallback(
    async (patternId: string): Promise<Set<string>> => {
      if (!companyId || !patternId) return new Set();
      try {
        const dictionaries = await getDictionariesByPatternId(companyId, patternId);
        return new Set(
          dictionaries
            .filter(d => d.dictionary_id !== initial?.dictionary_id)
            .map(d => d.search_item_id)
            .filter((id): id is string => !!id),
        );
      } catch {
        return new Set();
      }
    },
    [companyId, initial?.dictionary_id],
  );

  // モーダル表示中にパターンが変わったら使用済み項目（マスタ・他辞書）を再取得（閉じている間は取得しない）
  useEffect(() => {
    if (!open) return;
    let active = true;
    void fetchMasterUsedItemIds(selectedPatternId).then(ids => {
      if (active) setMasterUsedItemIds(ids);
    });
    void fetchDictionaryUsedItemIds(selectedPatternId).then(ids => {
      if (active) setDictionaryUsedItemIds(ids);
    });
    return () => {
      active = false;
    };
  }, [open, fetchMasterUsedItemIds, fetchDictionaryUsedItemIds, selectedPatternId]);

  const selectedPattern = patterns.find(p => p.patternId === selectedPatternId);
  const patternItems: PatternItem[] = useMemo(() => {
    if (!selectedPattern?.item_list) return [];
    return selectedPattern.item_list.flatMap((item: ItemListType) => {
      const parentItem = { id: item.id, label: item.parentValue };
      const childItems = item.children.map(child => ({
        id: child.id,
        label: `${item.parentValue} > ${child.childValue}`,
      }));
      return childItems.length > 0 ? childItems : [parentItem];
    });
  }, [selectedPattern]);

  const canSubmit =
    !!selectedPatternId &&
    !!searchItemId &&
    !!searchCsvColumn &&
    columnMappings.every(m => m.csv_column && m.item_id);

  const handleSubmit = async (): Promise<void> => {
    if (!canSubmit) return;
    const patternName = selectedPattern?.pattern_name ?? '';
    const searchItemLabel = patternItems.find(i => i.id === searchItemId)?.label ?? '';
    setSubmitting(true);
    try {
      await onSubmit({
        patternId: selectedPatternId,
        patternName,
        searchItemId,
        searchItemLabel,
        searchCsvColumn,
        columnMappings,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} sx={styles.dialog}>
      <CustomLoading loading={submitting} />
      <Box sx={styles.dialogHeader}>
        <Typography sx={styles.dialogTitle}>
          {initial ? 'パターン設定の編集' : 'パターン設定を追加'}
        </Typography>
        <IconButton onClick={onClose} sx={styles.iconButton}>
          <Close sx={styles.closeIcon} />
        </IconButton>
      </Box>

      <Box sx={styles.fileSummary}>
        <Typography sx={regularSm}>対象辞書：{fileName}</Typography>
      </Box>

      {csvColumns.length > 0 && (
        <Box sx={{ marginBottom: '16px' }}>
          <Typography sx={{ ...boldMd, marginBottom: '4px', color: gray80 }}>
            検索対象項目（辞書項目）
          </Typography>
          <Typography
            sx={{ fontSize: '11px', color: gray80, lineHeight: 1.4, marginBottom: '4px' }}
          >
            辞書CSVのどのカラムであいまい検索するか
          </Typography>
          <CustomSelectField
            labelText=""
            list={csvColumns.map(col => ({ option: col, value: col }))}
            value={searchCsvColumn}
            placeholder="選択してください"
            onChange={(e): void => setSearchCsvColumn(String(e.target.value))}
            formMb="4px"
            height="40px"
            width="100%"
          />
          <Typography sx={{ fontSize: '11px', color: red50, lineHeight: 1.4 }}>
            ※ この項目を基準にあいまい検索を行います。
          </Typography>
        </Box>
      )}

      <CustomSelectField
        labelText="読取パターン"
        list={patterns.map(p => ({ option: p.pattern_name, value: p.patternId }))}
        value={selectedPatternId}
        placeholder="選択してください"
        onChange={(e): void => {
          setSelectedPatternId(String(e.target.value));
          setSearchItemId('');
          setColumnMappings([]);
        }}
        formMb="16px"
        height="40px"
        width="100%"
      />

      {selectedPatternId && patternItems.length > 0 && (
        <Box sx={{ marginBottom: '16px' }}>
          <Typography sx={{ ...boldMd, marginBottom: '4px', color: gray80 }}>
            変換対象項目（パターン項目）
          </Typography>
          <Typography
            sx={{ fontSize: '11px', color: gray80, lineHeight: 1.4, marginBottom: '4px' }}
          >
            読取結果のどの値で検索するか
          </Typography>
          <CustomSelectField
            labelText=""
            list={patternItems.map(i => {
              const usedByMaster = masterUsedItemIds.has(i.id);
              const usedByDictionary = dictionaryUsedItemIds.has(i.id);
              const suffix = usedByMaster
                ? '（マスタ設定済み）'
                : usedByDictionary
                  ? '（辞書変換設定済み）'
                  : '';
              return {
                option: `${i.label}${suffix}`,
                value: i.id,
                disabled: usedByMaster || usedByDictionary,
              };
            })}
            value={searchItemId}
            placeholder="選択してください"
            onChange={(e): void => setSearchItemId(String(e.target.value))}
            formMb="0px"
            height="40px"
            width="100%"
          />
        </Box>
      )}

      {csvColumns.length > 0 && selectedPatternId && (
        <Box sx={{ marginBottom: '16px' }}>
          <Typography sx={{ ...boldMd, marginBottom: '4px', color: gray80 }}>
            取得項目（辞書項目） → 格納先項目（パターン項目）
          </Typography>
          <Typography
            sx={{ fontSize: '11px', color: gray80, lineHeight: 1.4, marginBottom: '8px' }}
          >
            一致した行から取り出す辞書項目と、どのパターン項目に格納するかを設定します
          </Typography>
          {columnMappings.map((mapping, index) => (
            <Box
              key={index}
              sx={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px' }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <CustomSelectField
                  labelText=""
                  list={csvColumns.map(col => ({ option: col, value: col }))}
                  value={mapping.csv_column}
                  placeholder="取得項目（辞書項目）"
                  onChange={(e): void => {
                    const updated = [...columnMappings];
                    updated[index] = { ...updated[index], csv_column: String(e.target.value) };
                    setColumnMappings(updated);
                  }}
                  formMb="0px"
                  height="40px"
                  width="100%"
                />
              </Box>
              <Typography sx={{ ...regularSm, color: gray80 }}>→</Typography>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <CustomSelectField
                  labelText=""
                  // 変換キー自身も選べる（#3339）。指定すると変換結果の値で
                  // 変換キー欄を上書きする。未指定なら OCR 読取値のまま（#2915）。
                  // 元の読取値は ocr_value に退避されるので「OCR結果：」表示は残る。
                  list={patternItems.map(i => ({ option: i.label, value: i.id }))}
                  value={mapping.item_id}
                  placeholder="格納先項目（パターン項目）"
                  onChange={(e): void => {
                    const updated = [...columnMappings];
                    const selectedItem = patternItems.find(i => i.id === e.target.value);
                    updated[index] = {
                      ...updated[index],
                      item_id: String(e.target.value),
                      item_label: selectedItem?.label || '',
                    };
                    setColumnMappings(updated);
                  }}
                  formMb="0px"
                  height="40px"
                  width="100%"
                />
              </Box>
              <IconButton
                onClick={(): void => {
                  setColumnMappings(columnMappings.filter((_, i) => i !== index));
                }}
                sx={{ padding: '4px' }}
              >
                <RemoveCircleOutlineIcon sx={{ color: orange50, fontSize: '20px' }} />
              </IconButton>
            </Box>
          ))}
          <Button
            startIcon={<AddCircleOutlineIcon />}
            onClick={(): void => {
              setColumnMappings([
                ...columnMappings,
                { csv_column: '', item_id: '', item_label: '' },
              ]);
            }}
            sx={{
              ...secondaryButtonBackgroundColor,
              height: '36px',
              marginTop: '4px',
              '& .MuiButton-icon': { marginRight: '4px' },
            }}
          >
            マッピング追加
          </Button>
        </Box>
      )}

      <Box sx={styles.buttonWrapper}>
        <Button sx={styles.cancelButton} onClick={onClose} disabled={submitting}>
          <Typography sx={styles.cancelButtonText}>キャンセル</Typography>
        </Button>
        <Button
          sx={styles.submitButton}
          onClick={(): void => {
            void handleSubmit();
          }}
          disabled={!canSubmit || submitting}
        >
          <Typography sx={styles.submitButtonText}>保存</Typography>
        </Button>
      </Box>
    </Dialog>
  );
};

export default DictionaryMappingFormModal;
